/**
 * Admission: can this person come in today, and what is still owed.
 *
 * Spec: `concierge-desktop/screens/visitor-details.md`, Decisions (13 Oct),
 * and the Paper board "Admission · All states · Docked and expanded".
 *
 * The card's title says what is going on; its colour says yes or no. The card
 * never names a requirement: customers name them in Settings, so length and
 * language are unknown. Names appear only in the opened list and the
 * expanded list, where rows wrap.
 *
 * Pure: the panel hands in the attendance status and the requirement rows
 * (from `requirementsOwed` and `gate`, or the demo store) and draws what
 * comes back.
 */

import type { OverrideRecord } from '../visit-row';

export type { OverrideRecord };

/** `AttendanceStatus` on the API, the states a visitor-day moves through. */
export type AttendanceStatus =
  | 'scheduled'
  | 'expected'
  | 'atDesk'
  | 'checkingIn'
  | 'onSite'
  | 'checkedOut'
  | 'noShow'
  | 'expired';

/** How a requirement stands, in the panel's words (finer API states fold in). */
export type RequirementMark =
  /** Held, in date, accepted. */
  | 'met'
  /** Held, and lapses within the warning window. */
  | 'expiring'
  /** Held once, now out of date. */
  | 'expired'
  /** An upload waiting for the desk to look at it. */
  | 'review'
  /** Nothing held, or not held the way this requirement accepts. */
  | 'missing';

/** What the desk can do about one row, right there. */
export type RequirementAction =
  /** Look at an upload and approve it (`witnessQuestionnaireAnswer`). */
  | 'review'
  /** Record that staff sighted the original (`recordCredential`, staff check). */
  | 'markSeen';

/**
 * A file the visitor uploaded for a requirement, waiting for the desk.
 *
 * The API doesn't store visitor uploads yet (`NEEDS_API.uploads`): in
 * platform mode `upload` is null and the review shows no file. Any kind of
 * file can arrive (a certificate, a licence, a card); the review shows PDFs
 * in the browser's viewer and images as they are.
 */
export interface UploadedFile {
  /** A short-lived read URL (platform), or a demo file under `/demo/uploads`. */
  readonly url: string;
  readonly fileName: string;
  readonly mimeType: string;
  /** ISO instant. */
  readonly uploadedAt: string;
  readonly uploadedBy: string | null;
}

export interface RequirementRow {
  readonly id: string;
  /** The customer's own name for it, from Settings. */
  readonly label: string;
  readonly mark: RequirementMark;
  /** Refuses the door while owed. A soft one is chased, never enforced. */
  readonly blocking: boolean;
  /** The row's second line, already worded ("Expired 11 Jan · redo at the kiosk"). */
  readonly detail: string;
  /** The action the desk can complete; none for what the visitor does at the kiosk. */
  readonly action: RequirementAction | null;
  /** The visitor's file, on a row waiting for review. */
  readonly upload?: UploadedFile | null;
}

export interface AdmissionInput {
  readonly status: AttendanceStatus;
  readonly requirements: readonly RequirementRow[];
  /** A photo is required for this reason and none is held (`GatePhoto.owed`). */
  readonly photoOwed: boolean;
  /** Days until the soonest held requirement lapses, when one is expiring. */
  readonly expiringInDays?: number | null;
  readonly override?: OverrideRecord | null;
  /** ISO instants for the signed-in and signed-out titles. */
  readonly arrivedAt?: string | null;
  readonly departedAt?: string | null;
}

export type AdmissionKey =
  | 'ready'
  | 'expiring'
  | 'missing'
  | 'photo'
  | 'signingIn'
  | 'signedIn'
  | 'override'
  | 'signedOut'
  | 'didntArrive';

/** The card's colour: dot, border and chip. */
export type AdmissionTone = 'success' | 'warning' | 'error' | 'neutral';

export interface AdmissionSummary {
  readonly key: AdmissionKey;
  readonly title: string;
  /** The second line, or null for a state the title says in full. */
  readonly sub: string | null;
  readonly tone: AdmissionTone;
  /** The chip the expanded list's head carries. */
  readonly chip: string;
  /** Sign in needs a reason (`overrideAndSignIn`). */
  readonly blocked: boolean;
  /** The visit is over: ticks read as a record, in grey. */
  readonly past: boolean;
  /** Rows in the order the list shows them: what's owed first, then the rest. */
  readonly rows: readonly RequirementRow[];
}

/** Hours and minutes in the visitor's day, "08:43". */
export function clock(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** "11 Jan" — the short date the rows use. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** List order: what blocks first, then what's waiting, then held, then optional. */
function rank(r: RequirementRow): number {
  if (r.mark === 'met') return 3;
  if (r.mark === 'expiring') return 2;
  if (r.mark === 'review') return 1;
  return r.blocking ? 0 : 4;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * The row id of the photo step. The gate owes a photo apart from the
 * requirements (`GatePhoto.owed`); the list shows it as a row, the card as
 * "Photo needed".
 */
export const PHOTO_ROW_ID = 'photo';

/** A requirement that is owed and refuses the door. */
export function isBlockingOwed(row: RequirementRow): boolean {
  return row.blocking && row.mark !== 'met' && row.mark !== 'expiring';
}

export function admission(input: AdmissionInput): AdmissionSummary {
  const rows = [...input.requirements].sort((a, b) => rank(a) - rank(b));
  const owed = rows.filter((r) => r.id !== PHOTO_ROW_ID && isBlockingOwed(r)).length;
  const base = { rows, blocked: false, past: false };

  switch (input.status) {
    case 'checkedOut':
      return {
        ...base,
        key: 'signedOut',
        title: $localize`:@@visitors-admission-signed-out:Signed out ${clock(input.departedAt)}:time:`,
        sub: null,
        tone: 'neutral',
        chip: $localize`:@@visitors-admission-chip-signed-out:Signed out`,
        past: true,
      };
    case 'noShow':
    case 'expired':
      return {
        ...base,
        key: 'didntArrive',
        title: $localize`:@@visitors-admission-didnt-arrive:Didn't arrive`,
        sub: null,
        tone: 'neutral',
        chip: $localize`:@@visitors-admission-didnt-arrive:Didn't arrive`,
        past: true,
      };
    case 'onSite':
      if (input.override) {
        const unmet = Math.max(owed, 1);
        return {
          ...base,
          key: 'override',
          title: $localize`:@@visitors-admission-override:Admitted by override`,
          sub: $localize`:@@visitors-admission-override-sub:${unmet}:count: ${plural(unmet, 'requirement', 'requirements')}:noun: not met`,
          tone: 'warning',
          chip: $localize`:@@visitors-admission-override:Admitted by override`,
        };
      }
      return {
        ...base,
        key: 'signedIn',
        title: $localize`:@@visitors-admission-signed-in:Signed in ${clock(input.arrivedAt)}:time:`,
        sub: null,
        tone: 'success',
        chip: $localize`:@@visitors-admission-chip-signed-in:Signed in`,
      };
    case 'atDesk':
    case 'checkingIn':
      // "At the desk" is just signing in (Rahim, 13 Oct).
      return {
        ...base,
        key: 'signingIn',
        title: $localize`:@@visitors-admission-signing-in:Signing in`,
        sub: $localize`:@@visitors-admission-signing-in-sub:At the kiosk`,
        tone: 'neutral',
        chip: $localize`:@@visitors-admission-signing-in:Signing in`,
      };
    default:
      break;
  }

  // Before arrival: scheduled or expected.
  if (owed > 0) {
    return {
      ...base,
      key: 'missing',
      title: $localize`:@@visitors-admission-missing:Missing requirements`,
      sub: $localize`:@@visitors-admission-missing-sub:${owed}:count: ${plural(owed, 'requirement', 'requirements')}:noun:`,
      tone: 'error',
      chip: $localize`:@@visitors-admission-missing:Missing requirements`,
      blocked: true,
    };
  }
  if (input.photoOwed) {
    return {
      ...base,
      key: 'photo',
      title: $localize`:@@visitors-admission-photo:Photo needed`,
      sub: $localize`:@@visitors-admission-photo-sub:Take it at the kiosk`,
      tone: 'error',
      chip: $localize`:@@visitors-admission-photo:Photo needed`,
      blocked: true,
    };
  }
  const expiring = rows.filter((r) => r.mark === 'expiring').length;
  if (expiring > 0) {
    const days = input.expiringInDays ?? null;
    return {
      ...base,
      key: 'expiring',
      title: $localize`:@@visitors-admission-expiring:Expiring soon`,
      sub:
        days === null
          ? $localize`:@@visitors-admission-expiring-sub-nodays:${expiring}:count: ${plural(expiring, 'requirement expires', 'requirements expire')}:noun: soon`
          : $localize`:@@visitors-admission-expiring-sub:${expiring}:count: ${plural(expiring, 'requirement expires', 'requirements expire')}:noun: in ${days}:days: ${plural(days, 'day', 'days')}:unit:`,
      tone: 'warning',
      chip: $localize`:@@visitors-admission-expiring:Expiring soon`,
    };
  }
  return {
    ...base,
    key: 'ready',
    title: $localize`:@@visitors-admission-ready:Ready to sign in`,
    sub: null,
    tone: 'success',
    chip: $localize`:@@visitors-admission-ready:Ready to sign in`,
  };
}
